import Link from 'next/link'
import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import TrustBand from '@comp/home/trust-band'
import FinalCta from '@comp/home/final-cta'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import Button from '@comp/ui/button'
import {
  integrations,
  integrationSlug,
  getIntegrationBySlug,
  relatedIntegrations,
  protocolLabels,
  Integration,
} from '@comp/integrations/integration-data'

// ---------------------------------------------------------------------------
// Localized, protocol-aware copy templates. Every page links to the REAL
// help-center article for the app: docs-shaped content, no dead ends.
// ---------------------------------------------------------------------------

type Lang = 'en' | 'tr'

const ui = {
  eyebrow: { en: 'Integration', tr: 'Entegrasyon' },
  title: {
    en: (app: string) => `${app} Single Sign-On with Monosign`,
    tr: (app: string) => `Monosign ile ${app} Tek Oturum Açma`,
  },
  titleRadiusOnly: {
    en: (app: string) => `${app} MFA & authentication via Monosign`,
    tr: (app: string) => `Monosign ile ${app} MFA ve kimlik doğrulama`,
  },
  seoTitle: {
    en: (app: string) => `${app} SSO Integration | Monosign | Monofor`,
    tr: (app: string) => `${app} SSO Entegrasyonu | Monosign | Monofor`,
  },
  seoDescription: {
    en: (app: string, protos: string) =>
      `Connect ${app} to Monosign over ${protos}: step-by-step setup, MFA on every login, automated provisioning, and audit-ready logs. Try it in a free trial tenant.`,
    tr: (app: string, protos: string) =>
      `${app} uygulamasını ${protos} üzerinden Monosign'a bağlayın: adım adım kurulum, her girişte MFA, otomatik kullanıcı sağlama ve denetime hazır loglar. Ücretsiz deneme ortamında test edin.`,
  },
  heroBody: {
    en: (app: string) =>
      `Put ${app} behind your identity plane: one login, MFA enforced, access granted and revoked automatically with the joiner-mover-leaver lifecycle; and every authentication logged, tamper-evident.`,
    tr: (app: string) =>
      `${app} uygulamasını kimlik katmanınızın arkasına alın: tek giriş, zorunlu MFA, işe giriş-geçiş-ayrılış yaşam döngüsüyle otomatik verilen ve kapatılan erişim; kurcalamaya karşı korumalı şekilde loglanan her kimlik doğrulama.`,
  },
  ctaTrial: { en: 'Start free trial →', tr: 'Ücretsiz deneme başlat →' },
  ctaDocs: { en: 'Open setup docs', tr: 'Kurulum dokümanını aç' },
  ctaDocsGeneric: { en: 'Browse the help center', tr: 'Yardım merkezine göz atın' },
  genericNote: {
    en: (app: string) =>
      `A dedicated ${app} article is on its way. The generic setup guides in our help center cover the same flow, and the in-console template does most of the work.`,
    tr: (app: string) =>
      `${app} uygulamasına özel makale yolda. Yardım merkezimizdeki genel kurulum rehberleri aynı akışı anlatır; konsoldaki şablon işin çoğunu halleder.`,
  },
  trialNote: {
    en: (app: string) =>
      `Spin up a full trial tenant in five minutes and connect ${app} today: no credit card, no sales call.`,
    tr: (app: string) =>
      `Beş dakikada tam bir deneme ortamı kurun ve ${app} bağlantısını bugün yapın: kredi kartı yok, satış görüşmesi yok.`,
  },
  protocolsLabel: { en: 'Supported protocols', tr: 'Desteklenen protokoller' },
  stepsEyebrow: { en: 'Setup', tr: 'Kurulum' },
  stepsTitle: {
    en: (app: string) => `${app} to production in three steps.`,
    tr: (app: string) => `${app} üç adımda üretimde.`,
  },
  stepsNote: {
    en: 'The help-center article walks through every screen with screenshots.',
    tr: 'Yardım merkezi makalesi her ekranı ekran görüntüleriyle anlatır.',
  },
  faqEyebrow: { en: 'FAQ', tr: 'SSS' },
  faqTitle: { en: 'Common questions.', tr: 'Sık sorulan sorular.' },
  relatedEyebrow: { en: 'More integrations', tr: 'Diğer entegrasyonlar' },
  relatedTitle: {
    en: 'Works with the rest of your stack too.',
    tr: 'Yığınınızın geri kalanıyla da çalışır.',
  },
  allIntegrations: { en: 'Browse all integrations →', tr: 'Tüm entegrasyonlara göz atın →' },
  viewIntegration: { en: 'View integration', tr: 'Entegrasyonu görüntüle' },
}

function steps(app: string, hasSaml: boolean, hasOidc: boolean, hasRadius: boolean, lang: Lang) {
  const proto = hasSaml ? 'SAML' : hasOidc ? 'OIDC' : hasRadius ? 'RADIUS' : null
  if (lang === 'tr') {
    return [
      {
        title: `Monosign'da ${app} uygulamasını ekleyin`,
        body: proto
          ? `Monosign konsolunda uygulama kataloğundan ${app} şablonunu seçin. ${proto} yapılandırması (metadata, sertifikalar ve uç noktalar) otomatik oluşturulur.`
          : `Monosign konsolunda uygulama kataloğundan ${app} şablonunu seçin; bağlantı yapılandırması otomatik oluşturulur.`,
      },
      {
        title: `${app} tarafında güveni kurun`,
        body: proto === 'RADIUS'
          ? `${app} üzerinde Monosign RADIUS sunucusunu kimlik doğrulama kaynağı olarak tanımlayın; MFA meydan okumaları Access-Challenge ile akar.`
          : `Monosign'ın ürettiği metadata/istemci bilgilerini ${app} yönetim paneline ekleyin. Yardım merkezi makalesi alan alan gösterir.`,
      },
      {
        title: 'Test edin ve kullanıcılarınıza açın',
        body: `Test kullanıcısıyla uçtan uca giriş yapın, ardından erişimi Monosign'daki grup ve politikalarla yönetin. Her giriş MFA'lı ve loglanmış olur.`,
      },
    ]
  }
  return [
    {
      title: `Add ${app} in Monosign`,
      body: proto
        ? `Pick the ${app} template from the application catalog in the Monosign console. The ${proto} configuration (metadata, certificates, endpoints) is generated for you.`
        : `Pick the ${app} template from the application catalog in the Monosign console; the connection configuration is generated for you.`,
    },
    {
      title: `Establish trust on the ${app} side`,
      body: proto === 'RADIUS'
        ? `Point ${app} at the Monosign RADIUS server as its authentication source; MFA challenges flow over Access-Challenge.`
        : `Paste the metadata/client details Monosign generated into the ${app} admin panel. The help-center article shows every field.`,
    },
    {
      title: 'Test, then roll out',
      body: `Sign in end-to-end with a test user, then govern access with Monosign groups and policies. Every login is MFA-enforced and logged.`,
    },
  ]
}

function faqs(app: string, protos: string, lang: Lang) {
  if (lang === 'tr') {
    return [
      {
        q: `${app} için Monosign hangi protokolleri destekler?`,
        a: `Bu entegrasyon ${protos} üzerinden çalışır. Monosign ayrıca platform genelinde SAML 2.0, OIDC/OAuth 2.0, SCIM, LDAP, RADIUS ve Kerberos destekler; ${app} yanında yığınınızın kalanı da aynı kimlik katmanına bağlanır.`,
      },
      {
        q: `${app} girişlerine MFA ekleyebilir miyim?`,
        a: `Evet. ${app} Monosign'a bağlandığında her giriş, politikanıza göre MFA'dan geçer: TOTP, push bildirimi veya kimlik avına dirençli geçiş anahtarları (FIDO2). Risk sinyaline göre uyarlanabilir ek doğrulama da uygulanır.`,
      },
      {
        q: 'Kurulum ne kadar sürer?',
        a: `Çoğu ekip için ${app} bağlantısı bir saatten kısa sürer: şablon hazır, adımlar dokümante. Ücretsiz deneme ortamında üretime dokunmadan test edebilirsiniz.`,
      },
      {
        q: `${app} erişimini kim aldığında otomatik kapanır mı?`,
        a: `Evet. İşe giriş-geçiş-ayrılış yaşam döngüsü, ${app} erişimini role göre verir ve kişi ayrıldığı gün otomatik kapatır; bayat hesap kalmaz, denetim izi hazır olur.`,
      },
    ]
  }
  return [
    {
      q: `Which protocols does Monosign support for ${app}?`,
      a: `This integration works over ${protos}. Monosign also supports SAML 2.0, OIDC/OAuth 2.0, SCIM, LDAP, RADIUS, and Kerberos platform-wide, so the rest of your stack connects to the same identity plane alongside ${app}.`,
    },
    {
      q: `Can I enforce MFA on ${app} logins?`,
      a: `Yes. Once ${app} is behind Monosign, every login passes your MFA policy: TOTP, push, or phishing-resistant passkeys (FIDO2), with adaptive step-up when risk signals change.`,
    },
    {
      q: 'How long does setup take?',
      a: `For most teams, connecting ${app} takes under an hour: the template is pre-built and the steps are documented. You can test it in a free trial tenant without touching production.`,
    },
    {
      q: `Is ${app} access revoked automatically when someone leaves?`,
      a: `Yes. Joiner-mover-leaver lifecycle grants ${app} access by role and revokes it the day someone leaves: no stale accounts, and the audit trail is ready for review.`,
    },
  ]
}

type Props = { slug: string; locale: string }

export default function IntegrationPage({ slug, locale }: Props) {
  const integration = getIntegrationBySlug(slug) as Integration
  const lang: Lang = locale === 'tr' ? 'tr' : 'en'
  const basePath = lang === 'tr' ? '/tr' : ''
  const app = integration.name
  const protos = integration.protocols.map((p) => protocolLabels[p]).join(' / ')
  const hasSaml = integration.protocols.includes('saml')
  const hasOidc = integration.protocols.includes('oidc')
  const hasRadius = integration.protocols.includes('radius')
  const onlyRadius = hasRadius && !hasSaml && !hasOidc
  const title = onlyRadius ? ui.titleRadiusOnly[lang](app) : ui.title[lang](app)
  const pageSteps = steps(app, hasSaml, hasOidc, hasRadius, lang)
  const pageFaqs = faqs(app, protos, lang)
  const related = relatedIntegrations(integration)
  const docsHref = integration.href ?? 'https://help.monofor.com'
  const docsLabel = integration.href ? ui.ctaDocs[lang] : ui.ctaDocsGeneric[lang]

  return (
    <Layout>
      <Seo
        title={ui.seoTitle[lang](app)}
        description={ui.seoDescription[lang](app, protos)}
        path={`${basePath}/integrations/${slug}`}
      />
      <Head>
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}/integrations/${slug}`} />
        <link rel="alternate" hrefLang="tr" href={`${SITE_URL}/tr/integrations/${slug}`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/integrations/${slug}`} />
      </Head>
      <JsonLd
        id="integration-faq"
        data={{
          '@type': 'FAQPage',
          mainEntity: pageFaqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }}
      />
      <main className="bg-surface text-ink-primary">
        {/* Hero */}
        <Section tone="default" density="compact" className="relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/60 blur-3xl" />
            <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/50 blur-3xl" />
          </div>
          <Container size="xl" className="relative">
            <div className="max-w-3xl">
              <Eyebrow tone="brand">
                {ui.eyebrow[lang]} · {integration.category}
              </Eyebrow>
              <div className="mt-6">
                <Heading as="h1" size="display-xl">
                  {title}
                </Heading>
              </div>
              <div className="mt-8 max-w-2xl">
                {integration.description && (
                  <Text size="lg" tone="tertiary" className="mb-4">
                    {integration.description}
                  </Text>
                )}
                <Text size="xl" tone="secondary">
                  {ui.heroBody[lang](app)}
                </Text>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
                  {ui.protocolsLabel[lang]}:
                </span>
                {integration.protocols.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center rounded-pill border border-line-subtle bg-surface-subtle px-3 py-1 text-body-sm text-ink-secondary"
                  >
                    {protocolLabels[p]}
                  </span>
                ))}
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Button href="/try/now" variant="primary" size="lg">
                  {ui.ctaTrial[lang]}
                </Button>
                <Button href={docsHref} variant="outline" size="lg">
                  {docsLabel}
                </Button>
              </div>
              <div className="mt-6 text-body-sm text-ink-tertiary">
                {ui.trialNote[lang](app)}
              </div>
            </div>
          </Container>
        </Section>

        {/* Steps */}
        <Section tone="subtle" density="default">
          <Container size="md">
            <Eyebrow tone="accent">{ui.stepsEyebrow[lang]}</Eyebrow>
            <Heading as="h2" size="display-lg" className="mt-5">
              {ui.stepsTitle[lang](app)}
            </Heading>
            <Text size="lg" tone="secondary" className="mt-5">
              {integration.href ? ui.stepsNote[lang] : ui.genericNote[lang](app)}{' '}
              <a
                href={docsHref}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-brand-700 underline-offset-4 hover:underline"
              >
                {docsLabel} →
              </a>
            </Text>
            <ol className="mt-10 space-y-5">
              {pageSteps.map((step, i) => (
                <li
                  key={step.title}
                  className="grid gap-4 rounded-2xl border border-line-subtle bg-surface p-6 shadow-card lg:grid-cols-[56px_1fr]"
                >
                  <div className="text-display-sm font-medium text-brand-900/40 tabular-nums">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div>
                    <div className="text-body-lg font-medium text-ink-primary">
                      {step.title}
                    </div>
                    <Text size="md" tone="secondary" className="mt-2">
                      {step.body}
                    </Text>
                  </div>
                </li>
              ))}
            </ol>
          </Container>
        </Section>

        <TrustBand />

        {/* FAQ */}
        <Section tone="default" density="default">
          <Container size="md">
            <Eyebrow tone="brand">{ui.faqEyebrow[lang]}</Eyebrow>
            <Heading as="h2" size="display-lg" className="mt-5">
              {ui.faqTitle[lang]}
            </Heading>
            <dl className="mt-12 space-y-4">
              {pageFaqs.map((faq) => (
                <div
                  key={faq.q}
                  className="rounded-2xl border border-line-subtle bg-surface-subtle p-6 lg:p-8"
                >
                  <dt className="text-body-lg font-medium text-ink-primary">
                    {faq.q}
                  </dt>
                  <dd className="mt-3 text-body-md text-ink-secondary">
                    {faq.a}
                  </dd>
                </div>
              ))}
            </dl>
          </Container>
        </Section>

        {/* Related */}
        <Section tone="subtle" density="compact">
          <Container size="xl">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <Eyebrow tone="accent">{ui.relatedEyebrow[lang]}</Eyebrow>
                <Heading as="h2" size="display-md" className="mt-4">
                  {ui.relatedTitle[lang]}
                </Heading>
              </div>
              <Link
                href="/integrations"
                className="text-body-md font-medium text-brand-700 underline-offset-4 hover:underline"
              >
                {ui.allIntegrations[lang]}
              </Link>
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.name}
                  href={`/integrations/${integrationSlug(r.name)}`}
                  className="group flex items-center justify-between rounded-2xl border border-line-subtle bg-surface p-5 transition duration-300 ease-smooth hover:-translate-y-0.5 hover:border-line hover:shadow-card-hover"
                >
                  <div>
                    <div className="text-body-md font-medium text-ink-primary group-hover:text-brand-800">
                      {r.name}
                    </div>
                    <div className="mt-1 text-caption text-ink-tertiary">
                      {r.protocols.map((p) => protocolLabels[p]).join(' · ')}
                    </div>
                  </div>
                  <span
                    aria-hidden
                    className="text-brand-700 transition-transform group-hover:translate-x-0.5"
                  >
                    →
                  </span>
                </Link>
              ))}
            </div>
          </Container>
        </Section>

        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({
  params,
  locale,
}: {
  params: { slug: string }
  locale: string
}) {
  const integration = getIntegrationBySlug(params.slug)
  if (!integration) {
    return { notFound: true }
  }
  return { props: { slug: params.slug, locale: locale ?? 'en' } }
}

export async function getStaticPaths() {
  const paths = ['en', 'tr'].flatMap((locale) =>
    integrations.map((i) => ({
      params: { slug: integrationSlug(i.name) },
      locale,
    }))
  )
  return { paths, fallback: false }
}
